import moment from 'moment';

import config from '../../config';

import { dateFormat } from '../../util/date';
import { nl2br, getTextVisualWidth, hasFullWidthChars, wrapTextByVisualWidth, removeEmojis } from '../../util/text';
import { getHashKeys } from '../../util/security';
import { flat } from '../../util/array';
import { getDominantDirection, getTextDirection } from '../../util/bookScripts';

import { getMessageAttachments, MESSAGE_ATTACHMENT_VIEWER_DATA_SEPARATOR, MESSAGE_SYSTEM_SENDER_NAME } from './ConversationActions';
import { mergeTapbackReactions } from './tapbackReactions';
import { sortMessagesByDate, isMessageDateValid, isConversationBoundDateValid, resolveConversationDateBounds, realignImportedClockBounds, getWrittenClockMs } from './messagesSort';
import { parseReactionsFromCsv, isEncodedReactionsColumn } from './metaReactions';
import { notifyError } from '../../components/Error/ErrorBoundary';
import {
	PHOTO_TIER_LINES,
	collectPhotoStats,
	countContentPages,
	getIsolatedPhotoKeys,
	photoLayoutFitsTome,
	pickBookPhotoLayout,
	resolvePhotoAttachmentLayout,
} from './bookPhotoLayout';

export const BOOK_FORMATS_AVAILABLE = ['a5'];
export const BOOK_TYPES_AVAILABLE = ['book', 'bestof', 'pdf'];

const FONT_BASE_A5 = 10;
const FONT_BASE_A4 = 9;

const BOOK_PAGES_COUNT = false && process.env.NODE_ENV !== 'production' ? 5 : 400;
export const BOOK_PAGES_EXTRA_COUNT = 3; // Extra pages added to final pdf (cover, resume & last)

// Match CSS line-height + margin(top&bottom) + padding(top&bottom)
// Titre de date : ligne 1 + filet ::before (0.1 de trait + 0.35 de marge basse)
// + margin-top 0.7 + margin-bottom 0.3, soit 2.45 arrondi à 2.5. Mesuré à 25 px sur un
// livre réel par scripts/book-height-audit.js ; l'ancienne valeur 2.2 sous-estimait de
// 3 px, ce qui faisait déborder les pages pleines portant un titre.
const BOOK_LINE_DATE_TITLE_SIZE = 2.5;
// Nom d'expéditeur : hauteur fixe 0.6 (height + overflow hidden, jamais deux lignes)
// plus les marges 0.1 haut et 0.1 bas. Mesuré à 8 px.
const BOOK_LINE_MESSAGE_NAME_SIZE = 0.8;
const BOOK_MESSAGE_WRAPPER_SIZE = 0.2;
const BOOK_MESSAGE_BUBBLE_SIZE = 0.4;
const BOOK_LINE_MESSAGE_SIZE = 1; // 1.1
// Surcoût pagination quand une réaction déborde sous la bulle (badge positionné en absolu).
// Aligné sur la marge basse CSS ajoutée aux messages avec réaction : 0.5rem
// (cf. .messageWrapper--hasReactions), comme messageWrapperSize (0.2 ↔ 0.2rem).
// Compté une seule fois par message (voir getMessageLines).
const BOOK_MESSAGE_REACTION_SIZE = 0.5;

// 47 chars/line (vs 50) pour absorber emojis larges et caractères accentués sans débordement.
// Budget exprimé en largeur latine : un caractère CJK en consomme 2 (cf. getMessageLines).
const BOOK_LINE_MESSAGE_CHARS_COUNT = 47;
// Budget CJK plus serré : à chasse pleine, 47 unités dépassent la largeur utile de la
// bulle (messageContent − padding, max-width 95 %, nowrap). Mesure Chrome A5 ≈ 38.
const BOOK_LINE_MESSAGE_CHARS_COUNT_CJK = 38;
// Budget d'une photo S = 14 × font-base (140px sur A5). Choix assumé : 3 photos/page.
// M (22) et L (28) ne s'appliquent que si la période tient en un tome ; le CSS
// (messageImageWrapper.photo-m / .photo-l) doit rester aligné — cf. bookPhotoLayout.js.
const BOOK_LINE_MESSAGE_ATTACHMENT_SIZE = PHOTO_TIER_LINES.s;
// Carte média compacte : QR 5.1rem + wrapper + libellé + hint + domaine ≈ 7rem total
const BOOK_LINE_MESSAGE_ATTACHMENT_QRCODE_SIZE = 7;
const BOOK_LINE_MESSAGE_ATTACHMENTS_COUNT = 1; // 1 photo par emplacement (visibilité portrait/paysage)

export const BOOK_TITLE_MAX_LENGHT = 30;
export const BOOK_SUBTITLE_MAX_LENGHT = 45;
export const BOOK_RESUME_MAX_LENGHT = 1000;

/**
 * Géométrie d'une page de livre : source unique pour le PDF (book.helper FORMATS), le
 * HTML généré (min-height du template), la pagination (pageSize) et l'aperçu (viewer).
 *
 * PhantomJS convertit les millimètres du papier en pixels à 72 dpi. Mesure de contrôle :
 * scripts/book-height-audit.js sur un livre réel borne la capacité d'une page A5 entre
 * 479,5 px (page la plus haute rendue sans dommage) et 481,5 px (page la plus basse ayant
 * provoqué une feuille blanche), ce qui valide cette échelle au dixième de pixel.
 *
 * padding : valeurs de .page dans client/assets/css/print/book.css. Le script d'audit
 * vérifie qu'elles correspondent au rendu, il échoue si le CSS change sans cette table.
 */
const BOOK_PAGE_PX_PER_MM = 72 / 25.4;

export const BOOK_PAGE_GEOMETRY = {
    a5: { widthMm: 135, heightMm: 190, footerMm: 8, htmlWidth: 385, htmlHeight: 510, paddingTop: 25, paddingBottom: 10 },
    a4: { widthMm: 210, heightMm: 297, footerMm: 8, htmlWidth: 595, htmlHeight: 785, paddingTop: 25, paddingBottom: 10 },
};

function getPageGeometry(format) {
    return BOOK_PAGE_GEOMETRY[format] || BOOK_PAGE_GEOMETRY[BOOK_FORMATS_AVAILABLE[0]];
}

/** Hauteur de la feuille entière en pixels HTML, pied de page compris (aperçu). */
export function getBookSheetHeight(format = BOOK_FORMATS_AVAILABLE[0]) {
    return Math.round(getPageGeometry(format).heightMm * BOOK_PAGE_PX_PER_MM);
}

// Tailles d'affichage d'une page dans le viewer : la feuille entière, pied de page
// compris, pour que l'aperçu montre la même densité que le livre imprimé. Défini ici
// plutôt que dans le viewer parce que les miniatures de couverture s'en servent aussi,
// et qu'un import depuis le viewer créerait un cycle.
export const PAGE_SIZES = {
    a5: { width: BOOK_PAGE_GEOMETRY.a5.htmlWidth, height: getBookSheetHeight('a5') },
    a4: { width: BOOK_PAGE_GEOMETRY.a4.htmlWidth, height: getBookSheetHeight('a4') },
};

/** Hauteur imprimable : la feuille moins le pied de page ajouté par PhantomJS. */
export function getBookPrintableHeight(format = BOOK_FORMATS_AVAILABLE[0]) {
    const geometry = getPageGeometry(format);
    return (geometry.heightMm - geometry.footerMm) * BOOK_PAGE_PX_PER_MM;
}

/**
 * Place réellement disponible pour les messages : zone imprimable moins le padding de
 * .page. Au-delà, la page grandit et PhantomJS ajoute une feuille blanche.
 */
export function getBookPageCapacity(format = BOOK_FORMATS_AVAILABLE[0]) {
    const geometry = getPageGeometry(format);
    return getBookPrintableHeight(format) - geometry.paddingTop - geometry.paddingBottom;
}

/** Hauteurs HTML des pages livre (px) — alignées sur book.helper FORMATS.html */
export const BOOK_PAGE_HTML_HEIGHT = {
    a5: BOOK_PAGE_GEOMETRY.a5.htmlHeight,
    a4: BOOK_PAGE_GEOMETRY.a4.htmlHeight,
};

/** Marge laissée sous la capacité imprimable, pour absorber les arrondis de rendu. */
const BOOK_PAGE_SAFETY_MARGIN = 5;

/**
 * Budget vertical d'une page de messages.
 *
 * Deux plafonds, on retient le plus bas :
 * - la hauteur nominale du bloc HTML (min-height moins padding) : en restant dessous,
 *   aucune page ne grandit au-delà de sa taille annoncée ;
 * - la capacité imprimable mesurée, moins une marge de sécurité.
 *
 * Le pied de page n'est pas déduit : il est en position absolute (book.css), donc hors
 * du flux. L'ancienne valeur 462 lui réservait 7 px pour rien.
 */
export function getBookPageSize(format = BOOK_FORMATS_AVAILABLE[0]) {
    const geometry = getPageGeometry(format);
    const nominalHeight = geometry.htmlHeight - geometry.paddingTop - geometry.paddingBottom;
    const measuredCapacity = Math.floor(getBookPageCapacity(format)) - BOOK_PAGE_SAFETY_MARGIN;
    return Math.min(nominalHeight, measuredCapacity);
}

/** Caractères par ligne visuelle (Cormorant, padding préface) — estimation conservative A5. */
export const BOOK_RESUME_CHARS_PER_LINE = { a5: 34, a4: 46 };

/** Lignes visuelles max sur une page préface (A5 = référence validation saisie). */
export const BOOK_RESUME_MAX_VISUAL_LINES = { a5: 15, a4: 20 };

/**
 * Estime le nombre de lignes occupées (retours explicites + césure automatique des paragraphes longs).
 */
export function getResumeVisualLineCount(resume = '', format = 'a5') {
    const text = (resume || '').trim();
    if(!text) {
        return 0;
    }
    const charsPerLine = BOOK_RESUME_CHARS_PER_LINE[format] || BOOK_RESUME_CHARS_PER_LINE.a5;
    return text.split('\n').reduce((lines, line) => {
        const trimmed = line.trim();
        if(!trimmed) {
            return lines + 1;
        }
        // Préface CJK : même pondération visuelle que les bulles (idéogramme ≈ 2).
        const width = getTextVisualWidth(trimmed);
        const budget = hasFullWidthChars(trimmed)
            ? Math.max(1, Math.floor(charsPerLine * BOOK_LINE_MESSAGE_CHARS_COUNT_CJK / BOOK_LINE_MESSAGE_CHARS_COUNT))
            : charsPerLine;
        return lines + Math.max(1, Math.ceil(width / budget));
    }, 0);
}

/**
 * Score visuel préface — dominé par le nombre de lignes (contrainte hauteur page).
 */
export function getResumeVisualScore(resume = '', format = 'a5') {
    const lines = getResumeVisualLineCount(resume, format);
    const chars = (resume || '').trim().length;
    return (lines * 100) + chars;
}

/** Classe CSS de réduction progressive si la préface approche de la capacité d'une page. */
export function getResumeLengthClass(resume = '', format = 'a5') {
    const lines = getResumeVisualLineCount(resume, format);
    const thresholds = format === 'a4'
        ? { m: 10, l: 14, xl: 18 }
        : { m: 7, l: 11, xl: 14 };
    if(lines <= thresholds.m) {
        return 'resume-length-s';
    }
    if(lines <= thresholds.l) {
        return 'resume-length-m';
    }
    if(lines <= thresholds.xl) {
        return 'resume-length-l';
    }
    return 'resume-length-xl';
}

/** Préface tenable sur une page (validation saisie — seuil A5, le plus restrictif). */
export function isResumeWithinPageBounds(resume = '', format = 'a5') {
    const lines = getResumeVisualLineCount(resume, format || 'a5');
    const maxLines = BOOK_RESUME_MAX_VISUAL_LINES[format] || BOOK_RESUME_MAX_VISUAL_LINES.a5;
    return lines <= maxLines && (resume || '').trim().length <= BOOK_RESUME_MAX_LENGHT;
}

export const BOOK_CONSTANTS = {
    a5: {
        pagesCount: BOOK_PAGES_COUNT,
        pagesPerSheet: 1,
        pageSize: getBookPageSize('a5'), // 475 px : 510 de bloc HTML moins 35 de padding
        dateTitleSize: FONT_BASE_A5 * BOOK_LINE_DATE_TITLE_SIZE,
        messageNameSize: FONT_BASE_A5 * BOOK_LINE_MESSAGE_NAME_SIZE,
        messageWrapperSize: FONT_BASE_A5 * BOOK_MESSAGE_WRAPPER_SIZE,
        messageBubbleSize: FONT_BASE_A5 * BOOK_MESSAGE_BUBBLE_SIZE,
        messageReactionSize: FONT_BASE_A5 * BOOK_MESSAGE_REACTION_SIZE,
        messageLineSize: FONT_BASE_A5 * BOOK_LINE_MESSAGE_SIZE,
        messageCharsCount: BOOK_LINE_MESSAGE_CHARS_COUNT,
        attachmentSizes: {
			default: FONT_BASE_A5 * BOOK_LINE_MESSAGE_ATTACHMENT_SIZE,
			qrcode: FONT_BASE_A5 * BOOK_LINE_MESSAGE_ATTACHMENT_QRCODE_SIZE,
		},
        attachmentCountPerLine: BOOK_LINE_MESSAGE_ATTACHMENTS_COUNT,
    },
    a4: {
        pagesCount: BOOK_PAGES_COUNT * 2,
        pagesPerSheet: 2,
        // Format inactif (BOOK_FORMATS_AVAILABLE ne contient que a5) : on garde la valeur
        // historique plutôt que de recalibrer un chemin qu'aucun livre n'emprunte.
        pageSize: 700,
        dateTitleSize: FONT_BASE_A4 * BOOK_LINE_DATE_TITLE_SIZE,
        messageNameSize: FONT_BASE_A4 * BOOK_LINE_MESSAGE_NAME_SIZE,
        messageWrapperSize: 0, // FONT_BASE_A4 * BOOK_MESSAGE_WRAPPER_SIZE,
        messageBubbleSize: FONT_BASE_A4 * BOOK_MESSAGE_BUBBLE_SIZE,
        messageReactionSize: FONT_BASE_A4 * BOOK_MESSAGE_REACTION_SIZE,
        messageLineSize: FONT_BASE_A4 * BOOK_LINE_MESSAGE_SIZE,
        messageCharsCount: BOOK_LINE_MESSAGE_CHARS_COUNT,
        attachmentSizes: {
			default: FONT_BASE_A4 * BOOK_LINE_MESSAGE_ATTACHMENT_SIZE,
			qrcode: FONT_BASE_A4 * BOOK_LINE_MESSAGE_ATTACHMENT_QRCODE_SIZE,
		},
        attachmentCountPerLine: BOOK_LINE_MESSAGE_ATTACHMENTS_COUNT,
    },
};

export const COVER_IMAGE_SIZES = { width: 228, height: 304 }; // Rapport 3/4;

export { getCoverFlyleafColors } from './coverIncipit';

export const CONVERSATION_COVER_COLORS = {
    'outremer': { color: '#213E99', background: '#FFF200', back: { circle: { color: '#E6027E' } }, front: { circle: { color: '#FFED00', filename: 'jaune' } }, legacy: true },
    'jaune': { color: '#FFF200', text: '#333333', background: '#EC348C', back: { circle: { color: '#009FE3' } }, front: { circle: { color: '#E6027E', filename: 'magenta' } }, legacy: true },
    'magenta': { color: '#EC348C', background: '#FFF200', back: { circle: { color: '#009FE3' } }, front: { circle: { color: '#FFED00', filename: 'jaune' } }, legacy: true },
    'ciel': { color: '#009fe3', background: '#FFF200', back: { circle: { color: '#E6027E' } }, front: { circle: { color: '#FFED00', filename: 'jaune' } }, legacy: true },
    'green': { color: '#6fbc85', background: '#FFF200', back: { circle: { color: '#009FE3' } }, front: { circle: { color: '#FFED00', filename: 'jaune' } }, legacy: true },
    'white': { color: '#ffffff', text: '#000000', background: '#000000', back: { circle: { color: '#000000' } }, front: { circle: { color: '#000', filename: 'black' } }, legacy: true },
    'black': { order: 10, color: '#000', text: '#ffffff', background: '#ffffff', back: { circle: { color: '#ffffff' } }, front: { circle: { color: '#fff', filename: 'white' } }, logoSuffix: '-white', legacy: true },
    // Palette lumière — ordre inversé : le vert ouvre, puis une teinte par tome.
    'sauge': { order: 1, color: '#B0D8C8', text: '#21302B', background: '#ffffff' },
    'ciel-doux': { order: 2, color: '#B0D0E8', text: '#21302B', background: '#ffffff' },
    'peche': { order: 3, color: '#F0C8B0', text: '#21302B', background: '#ffffff' },
    'ivoire-atelier': { order: 4, color: '#F5F0E6', text: '#21302B', background: '#ffffff' },
    'lavande': { order: 5, color: '#C8BCE0', text: '#21302B', background: '#ffffff' },
    'blush': { order: 6, color: '#EBC4C4', text: '#21302B', background: '#ffffff' },
    'emeraude': { order: 81, color: '#004D40', text: '#FBF8F2', background: '#ffffff', legacy: true },
    'encre': { order: 82, color: '#2F3D38', text: '#FBF8F2', background: '#ffffff', legacy: true },
    'minuit': { order: 83, color: '#2A3442', text: '#FBF8F2', background: '#ffffff', legacy: true },
    'cognac': { order: 84, color: '#9A6B4F', text: '#FBF8F2', background: '#ffffff', legacy: true },
    'bordeaux': { order: 85, color: '#6E3A42', text: '#FBF8F2', background: '#ffffff', legacy: true },
    'blue-pastel': { order: 91, color: '#d3e3eb', background: '#ffffff', legacy: true },
    'green-pastel': { order: 92, color: '#7C9792', background: '#ffffff', legacy: true },
    'pink-pastel': { order: 93, color: '#fadfe8', background: '#ffffff', legacy: true },
    'orange-pastel': { order: 94, color: '#ff9a5c', background: '#ffffff', legacy: true },
    'red-pastel': { order: 95, color: '#bd454a', background: '#ffffff', legacy: true },
    'buttercream': { order: 96, color: '#EEE0CD', background: '#ffffff', legacy: true },
};

export const CONVERSATION_PARTICIPANT_COLORS = {
    // Bulles — ordre = priorité commerciale (défauts : blush-clair + ciel-clair)
    'blush-clair': { order: 1, color: '#F5D8D8', text: '#21302B' },
    'ciel-clair': { order: 2, color: '#C8DCF0', text: '#21302B' },
    'lavande-claire': { order: 3, color: '#DDD0EC', text: '#21302B' },
    'prune-claire': { order: 4, color: '#E0B8C4', text: '#21302B' },
    'peche-claire': { order: 5, color: '#F5DCC8', text: '#21302B' },
    'sauge-clair': { order: 6, color: '#C0E0D4', text: '#21302B' },
    'miel': { order: 7, color: '#EED8A8', text: '#21302B' },
    'perle': { order: 8, color: '#ECEAE4', text: '#21302B' },
    'emeraude-claire': { color: '#A8C0B6', text: '#21302B', legacy: true },
    'bleu-brume': { color: '#B4C8D4', text: '#21302B', legacy: true },
    'pierre': { color: '#C5C9C4', text: '#21302B', legacy: true },
    'rose-poussiere': { color: '#DFC4BE', text: '#21302B', legacy: true },
    'ardoise': { color: '#7A8A84', text: '#FBF8F2', legacy: true },
    'grey': { color: '#E5E5EA', text: '#333', legacy: true },
    'blue': { color: '#0B93F6', legacy: true },
    'green': { color: '#51e27f', text: '#fff', legacy: true },
    'pink': { color: '#f567f3', legacy: true },
    'blue-ice': { color: '#51bfe2', legacy: true },
    'brown': { color: '#9F1600', text: '#ffffff', legacy: true },
    'yellow': { color: '#f1f533', text: '#333', legacy: true },
    'black': { color: '#333', text: '#ffffff', legacy: true },
    'blue-pastel': { color: '#d3e3eb', text: '#333', legacy: true },
    'green-pastel': { color: '#7C9792', text: '#fff', legacy: true },
    'pink-pastel': { color: '#fadfe8', text: '#333', legacy: true },
    'orange-pastel': { color: '#ff9a5c', text: '#fff', legacy: true },
    'red-pastel': { color: '#bd454a', text: '#fff', legacy: true },
    'green-mint': { color: '#DCF8C6', text: '#333', legacy: true },
};

const DISPLAY_LOG = false && process.env.NODE_ENV !== 'production';

// ---------------------------------------------------------------------------
// Règles d'inclusion des messages dans le livre — SOURCE UNIQUE DE VÉRITÉ.
// Utilisées par generateBooks (génération) ET par countBookMessages (comptage
// affiché dans l'édition des dates), pour garantir des chiffres cohérents.
// ---------------------------------------------------------------------------

// Message dans la plage de dates du livre (bornes incluses).
// Comparaison sur l'heure écrite du message, jamais sur un fuseau.
export function isBookMessageInDateRange(message, start, end) {
	const time = message && message.date ? getWrittenClockMs(message.date) : null;
	if(time == null) {
		return false;
	}
	const startTime = start ? getWrittenClockMs(start) : null;
	const endTime = end ? getWrittenClockMs(end) : null;
	return (startTime == null || time >= startTime)
		&& (endTime == null || time <= endTime);
}

// Doublon consécutif : même date, même texte (si texte) et mêmes pièces jointes
// (si PJ) que le message précédent dans l'ordre chronologique.
export function isBookMessageDuplicate(message, previousMessage) {
	if(!previousMessage) {
		return false;
	}
	return !(
		previousMessage.date !== message.date
		|| (message.text && previousMessage.text !== message.text)
		|| ((message.attachments || []).length && (previousMessage.attachments || []).join('|') !== (message.attachments || []).join('|'))
	);
}

// Compte le nombre de messages / photos / médias qui seront réellement inclus dans
// le livre pour une plage de dates donnée : mêmes règles que generateBooks (dates +
// doublons consécutifs + messages masqués) et que getSubmessages (réactions tapback).
// Attend des messages déjà triés chronologiquement (invariant Redux / chargement
// CSV) : pas de re-tri ici, pour éviter O(n log n) après chaque masquage.
// Les bornes sont converties une seule fois au lieu de deux fois par message :
// le compteur est rejoué à chaque masquage, sur des livres à 200 000 messages.
export function countBookSelection(messages = [], dates = {}) {
	const empty = { messages: 0, photos: 0, medias: 0 };
	if(!messages || !messages.length) {
		return empty;
	}
	const aligned = realignImportedClockBounds((dates || {}).start, (dates || {}).end, messages);
	const startTime = getWrittenClockMs(aligned.start);
	const endTime = getWrittenClockMs(aligned.end);
	let messagesCount = 0;
	let photosCount = 0;
	let mediasCount = 0;
	for(let index = 0; index < messages.length; index += 1) {
		const message = messages[index];
		const time = message && message.date ? getWrittenClockMs(message.date) : null;
		if(
			time !== null
			&& (startTime === null || time >= startTime)
			&& (endTime === null || time <= endTime)
			&& !isBookMessageDuplicate(message, messages[index - 1])
			&& !message.isHidden
			&& !message.isTapbackSource
		) {
			messagesCount += 1;
			const attachments = normalizeMessageAttachments(message.attachments);
			for(let j = 0; j < attachments.length; j += 1) {
				const mediaType = getAttachmentMediaType(attachments[j]);
				if(mediaType === 'photo') {
					photosCount += 1;
				} else if(mediaType === 'video' || mediaType === 'audio') {
					mediasCount += 1;
				}
			}
		}
	}
	return { messages: messagesCount, photos: photosCount, medias: mediasCount };
}

// Raccourci historique : nombre de messages du livre uniquement.
export function countBookMessages(messages = [], dates = {}) {
	return countBookSelection(messages, dates).messages;
}

// Estimation du nombre de pages d'un livre sans le paginer réellement : total de lignes
// divisé par la hauteur d'une page, comme la sélection Best Of côté serveur
// (server/api/conversation/bestof.helper.js). Sert de garde-fou avant de réafficher des
// messages dans un Best Of déjà calibré, où le livre ne peut pas dépasser 400 pages.
//
// Ce n'est pas un substitut à getBookPages : ni sous-messages, ni coupure de page réelle,
// ni séparateurs Best Of. Une valeur absolue est donc approximative ; c'est la comparaison
// de deux estimations successives, avec le même barème, qui est fiable.
//
// options.isVisible permet de simuler un réaffichage sans toucher aux messages du store.
// Attend des messages déjà triés chronologiquement, comme countBookMessages.
export function estimateBookPages(messages = [], options = {}) {
	if(!messages || !messages.length) {
		return 0;
	}
	const { start = null, end = null, isVisible = null } = options;
	const format = options.format || BOOK_FORMATS_AVAILABLE[0];
	const bookFormat = BOOK_CONSTANTS[format] || BOOK_CONSTANTS[BOOK_FORMATS_AVAILABLE[0]];
	const aligned = realignImportedClockBounds(start, end, messages);
	const startTime = getWrittenClockMs(aligned.start);
	const endTime = getWrittenClockMs(aligned.end);

	// Mêmes règles d'inclusion que generateBooks : plage de dates, doublons consécutifs,
	// messages masqués. Le doublon se compare au message précédent du tableau complet.
	const kept = [];
	for(let index = 0; index < messages.length; index += 1) {
		const message = messages[index];
		const time = message && message.date ? getWrittenClockMs(message.date) : null;
		if(
			time !== null
			&& (startTime === null || time >= startTime)
			&& (endTime === null || time <= endTime)
			&& !isBookMessageDuplicate(message, messages[index - 1])
			&& (isVisible ? isVisible(message) : !message.isHidden)
		) {
			kept.push(message);
		}
	}

	let totalLines = 0;
	for(let index = 0; index < kept.length; index += 1) {
		const message = kept[index];
		// Message vide : la pagination le saute, il ne coûte donc rien ici non plus.
		if(message.text || (message.attachments || []).length) {
			totalLines += getMessageLines(message, kept[index - 1], kept[index + 1], { format }).count;
		}
	}

	return totalLines / bookFormat.pageSize;
}

// Nombre de messages traités entre deux respirations du thread. Volontairement court :
// avec des lots de 1000, Firefox gelait l'onglet (navigation impossible) sur les gros livres.
const GENERATION_CHUNK_SIZE = 250;

/** Rejet émis quand l'appelant a abandonné la génération (voir options.shouldContinue). */
export const BOOK_GENERATION_CANCELLED = 'BOOK_GENERATION_CANCELLED';

export function isBookGenerationCancelledError(error) {
	return !!error && error.message === BOOK_GENERATION_CANCELLED;
}

/**
 * Rend la main entre deux lots. Le délai 0 suffit : le navigateur en profite pour
 * traiter les clics et la navigation, sans allonger la génération côté serveur (PDF).
 */
function yieldToScheduler() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Abandon coopératif : seul l'aperçu client fournit shouldContinue (démontage de page).
 * La génération PDF serveur ne le passe jamais et ne peut donc pas être interrompue.
 */
function isGenerationCancelled(options) {
	const shouldContinue = (options || {}).shouldContinue;
	return typeof shouldContinue === 'function' && !shouldContinue();
}

export function generateBooks(conversation, messages = [], options = { type: BOOK_TYPES_AVAILABLE[0], format: BOOK_FORMATS_AVAILABLE[0], nbPagesByBook: null, divideEqually: false, isEstimation: false }, onProgress = percentile => {}) {
	DISPLAY_LOG && console.log('generateBooks', conversation);
	const bookFormat = BOOK_CONSTANTS[(options || {}).format || BOOK_FORMATS_AVAILABLE[0]];

	let nbPagesByBook = (options || {}).nbPagesByBook || bookFormat.pagesCount;
	const divideByPages = pages => {
		// console.log('divideByPages', pages);
		const pagesEdit = [...pages];
		const books = [];
		const booksIteration = Math.floor(pages.length / nbPagesByBook);
		if((options || {}).divideEqually) {
			// console.log('Divide equally', `${pages.length} / ${(booksIteration + 1)} = ${pages.length / (booksIteration + 1)} => ${Math.ceil(pages.length / (booksIteration + 1))}`);
			nbPagesByBook = Math.ceil(pages.length / (booksIteration + 1));
		}
		// console.log('nbPagesByBook', nbPagesByBook);
		for(let i = 0; i <= booksIteration; i += 1) {
			books.push({
				pages: pagesEdit.splice(0, nbPagesByBook),
			});
		}
		// console.log(`Génération de ${books.length} livre(s) (${messages.length} messages et ${books.reduce((total, book) => total + book.pages.reduce((subTotal, page) => subTotal + page.count, 0), 0)} lignes) en ${(new Date().getTime() - timestampStartProcess) / 1000} secondes`);
		// console.log(books);
		return { books, pages };
	};

	// Progression cumulative 0-100 : la barre de l'aperçu suit le travail réel.
	onProgress(2);

	let { start, end } = resolveConversationDateBounds(conversation.dates || {}, conversation.messagesBlocks || []);
	// Sans dates choisies, les bornes viennent des blocs d'import. On les ramène
	// à l'heure écrite des messages (voir realignImportedClockBounds).
	const storedDates = conversation.dates || {};
	const userChoseDates = isConversationBoundDateValid(storedDates.start) && isConversationBoundDateValid(storedDates.end);
	if(!userChoseDates) {
		({ start, end } = realignImportedClockBounds(start, end, messages));
	}
	if(false && process.env.NODE_ENV === 'development') {
		start = '2021-09-08T00:00:00.000Z';
		end = '2021-09-10T00:00:00.000Z';
	}

	console.log(`Conversation dates: From ${start} -> To ${end}`);
	messages = sortMessagesByDate(messages)
		.filter((message, index, array) => (
			// Mêmes règles que countBookMessages (source unique de vérité) :
			// plage de dates + exclusion des doublons consécutifs + messages masqués.
			message
			&& isBookMessageInDateRange(message, start, end)
			&& !isBookMessageDuplicate(message, array[index - 1])
			&& !message.isHidden
		));

	onProgress(5);

	// if(false && (options || {}).isEstimation) {
	//     console.log('ESTIMATED CALCULATION');
	//     console.log('TRY::: Book Calculation from lines count', messages.reduce((lines, message) => lines + ((message[(options || {}).format || 'a5'] || {}).linesCount || 0), 0), bookFormat.pageSize, bookFormat.pagesCount, Math.ceil(messages.reduce((lines, message) => lines + ((message[(options || {}).format || 'a5'] || {}).linesCount || 0), 0) / bookFormat.pageSize / bookFormat.pagesCount));
	//
	//     let linesInCurrentPage = 0;
	//     let messagesInCurrentPage = [];
	//     const pages = [];
	//     messages.forEach((message, i) => {
	//         i === 0 && getMessageLines(message, messages[i - 1], messages[i + 1], options, true);
	//         if(linesInCurrentPage >= bookFormat.pageSize || i === (messages.length - 1)) {
	//             pages.push({
	//                 messages: messagesInCurrentPage,
	//                 count: messagesInCurrentPage.length,
	//             });
	//             linesInCurrentPage = 0;
	//             messagesInCurrentPage = [];
	//         } else {
	//             linesInCurrentPage += (message[(options || {}).format || 'a5'] || {}).linesCount || 0;
	//             messagesInCurrentPage.push(message);
	//         }
	//     });
	//     return Promise.resolve(divideByPages(pages));
	// }

	console.log('REAL CALCULATION');
	return getBookPages(messages, options, percentile => onProgress(5 + (percentile * 0.95))).then(divideByPages);
}

function getBookPagesRecursive(messages, pages = [], options = {}, onProgress = () => {}, index = 0, messagesInPage = [], linesInCurrentPage = 0, pageSeenSenders = {}) {
	return new Promise((resolveRecursive, rejectRecursive) => {
		const bookFormat = BOOK_CONSTANTS[options.format || BOOK_FORMATS_AVAILABLE[0]];

		const message = messages[index];
		if(message) {
			// Message précédent réel, même s'il est resté sur la page précédente : le titre
			// de date doit être décidé sur la suite chronologique, pas sur le découpage en
			// pages. Le nom d'expéditeur, lui, est bien réaffiché à chaque page : son coût
			// est retiré de baseCount puis recalculé via pageSeenSenders.
			const prevMessage = messages[index - 1] || null;
			const nextMessage = messages[index + 1] || null;

			const messageLines = getMessageLines(
				message,
				prevMessage && prevMessage.id !== message.id ? prevMessage : null,
				nextMessage && nextMessage.id !== message.id ? nextMessage : null,
				options,
				false && DISPLAY_LOG,
			);
			const messageLinesCount = messageLines.count;

			DISPLAY_LOG && console.log(`Message N°${index}: ${linesInCurrentPage} lines in page // ${messagesInPage.length} msgs // ${messageLines.details.join(' + ')} = ${messageLines.count}`);

			if(messageLines.lines.filter(line => line).length || (message.attachments || []).length) {
				const isSystemMessage = message.isSystem || message.sender === MESSAGE_SYSTEM_SENDER_NAME;
				// Coût intrinsèque, sans le nom d'expéditeur (qui dépend du contexte de page).
				const baseCount = messageLinesCount - (messageLines.senderNameCount || 0);
				// Le nom n'est affiché qu'une fois par contact et par page (comme seenSenders du
				// template), et il est réaffiché en haut d'une nouvelle page → recalculé après flush.
				const computeNameCost = seen => (!isSystemMessage && !seen[message.sender] ? bookFormat.messageNameSize : 0);
				let effectiveCount = baseCount + computeNameCost(pageSeenSenders);

				// Look-ahead : on vide la page courante AVANT d'ajouter si l'ajout déborderait.
				// Si la page est vide, on ajoute quand même (impossible de couper un seul sous-message).
				if(messagesInPage.length > 0 && linesInCurrentPage + effectiveCount > bookFormat.pageSize) {
					DISPLAY_LOG && console.log(`
						Flush page ${pages.length} : ${linesInCurrentPage} lignes
						(look-ahead : +${effectiveCount} dépasserait ${bookFormat.pageSize})
						------
					`);
					pages.push({
						count: messagesInPage.length,
						messages: messagesInPage,
					});
					messagesInPage = [];
					linesInCurrentPage = 0;
					pageSeenSenders = {};
					// Nouvelle page : le nom d'expéditeur y est forcément réaffiché → re-budgété.
					effectiveCount = baseCount + computeNameCost(pageSeenSenders);
				}

				if(!isSystemMessage) {
					pageSeenSenders[message.sender] = true;
				}

				linesInCurrentPage += effectiveCount;
				messagesInPage.push({
					...message,
					linesCount: effectiveCount,
					photoSize: messageLines.photoSize || message.photoSize || null,
					// Décision transportée jusqu'au template : il affiche le titre si et
					// seulement si le budget l'a compté. Sans cela, les deux comparaient
					// des jours calculés différemment et la page débordait.
					showDateTitle: (messageLines.dateTitleCount || 0) > 0,
					detailsLines: process.env.NODE_ENV === 'development' ? messageLines.details : [],
				});
			}

			const recursive = () => resolveRecursive(getBookPagesRecursive(messages, pages, options, onProgress, index + 1, messagesInPage, linesInCurrentPage, pageSeenSenders));
			// DO NOT use setTimeout if not needed (Because of 5s execution time by message !!!)
			if(index % GENERATION_CHUNK_SIZE === 0) {
				// Avancement cumulatif : l'ancien pas fixe ne dépassait jamais la barre optimiste.
				onProgress(messages.length ? Math.min(100, (index / messages.length) * 100) : 100);
				if(isGenerationCancelled(options)) {
					rejectRecursive(new Error(BOOK_GENERATION_CANCELLED));
				} else {
					setTimeout(recursive, 0);
				}
			} else {
				recursive();
			}
			return;
		}

		DISPLAY_LOG && console.log(`Dernière page : ${messagesInPage.length} messages`);
		pages.push({
			count: messagesInPage.length,
			messages: messagesInPage,
		});

		onProgress(100);
		resolveRecursive(pages);
	});
}

function paginateBookPagesWithPhotoLayout(messagesToProcess, messagesWithLines, options, bookFormat, onProgress) {
	const fontBase = ((options || {}).format === 'a4') ? FONT_BASE_A4 : FONT_BASE_A5;
	const paginate = (layout, progressFrom, progressTo, isolatedPhotoKeys) => getBookPagesRecursive(
		messagesWithLines,
		[],
		{
			...options,
			photoTier: layout.photoTier,
			highlightIsolated: layout.highlightIsolated,
			isolatedPhotoKeys,
		},
		percentile => onProgress(progressFrom + ((percentile / 100) * (progressTo - progressFrom))),
	);

	// Le S est la référence : il tient toujours, et son nombre de pages dit combien
	// de marge reste jusqu'au plafond d'un tome. Les tailles au-dessus ne sont
	// retenues qu'après une pagination réelle qui reste dans ce tome.
	return paginate({ photoTier: 's', highlightIsolated: false }, 0, 50, new Set()).then(pagesS => {
		const contentPageCount = countContentPages(pagesS);
		let stats = collectPhotoStats(messagesToProcess);
		let isolatedPhotoKeys = getIsolatedPhotoKeys(messagesToProcess);
		// Filet : si le CSV source n'a pas été reconnu (CR, string |||), les sous-messages
		// ont déjà les PJ normalisées et affichées.
		if(!stats.photoCount) {
			stats = collectPhotoStats(messagesWithLines);
			isolatedPhotoKeys = getIsolatedPhotoKeys(messagesWithLines);
		}
		const { photoCount, isolatedPhotoCount } = stats;
		const layout = pickBookPhotoLayout({
			contentPageCount,
			photoCount,
			isolatedPhotoCount,
			pagesPerTome: bookFormat.pagesCount,
			pageSize: bookFormat.pageSize,
			fontBase,
		});
		console.log('GENERATION::: Photo layout', {
			contentPageCount,
			photoCount,
			isolatedPhotoCount,
			layout,
			editionType: options.type,
		});

		// Repli progressif : la taille visée, puis le seul coup de pouce aux photos
		// isolées, puis le standard si même cela ferait un tome de plus.
		const candidates = [];
		if(layout.photoTier !== 's') {
			candidates.push(layout);
		}
		if(layout.highlightIsolated) {
			candidates.push({ photoTier: 's', highlightIsolated: true });
		}
		if(!candidates.length) {
			return pagesS;
		}

		const tryNext = (index, progress) => {
			if(index >= candidates.length) {
				return Promise.resolve(pagesS);
			}
			const progressTo = 50 + ((50 / candidates.length) * (index + 1));
			return paginate(candidates[index], progress, progressTo, isolatedPhotoKeys).then(pages => (
				photoLayoutFitsTome(pages, bookFormat.pagesCount)
					? pages
					: tryNext(index + 1, progressTo)
			));
		};
		return tryNext(0, 50);
	});
}

export function getBookPages(allMessages, options = { type: BOOK_TYPES_AVAILABLE[0], format: BOOK_FORMATS_AVAILABLE[0] }, onProgress = percentile => {}) {
    return new Promise((resolve, reject) => {
        const bookFormat = BOOK_CONSTANTS[(options || {}).format || BOOK_FORMATS_AVAILABLE[0]];
		// Rattache les réactions tapback iOS à leur message d'origine (idempotent : sans effet
		// si déjà fusionné en amont ou si la conversation n'en contient pas).
		allMessages = mergeTapbackReactions(allMessages);
		(
			options.type === 'bestof' && !options.bestofPreSelected
			? getBestofMessagesSelection(allMessages, options)
			: Promise.resolve(allMessages)
		// Sous-messages : 0-30 % de la progression, pagination : 30-100 %.
		).then(messagesToProcess => getSubmessages(
			messagesToProcess,
			{ ...options, caller: 'getBookPages' },
			percentile => onProgress(percentile * 0.3),
		).then(messagesWithLines => paginateBookPagesWithPhotoLayout(
			messagesToProcess,
			messagesWithLines,
			options,
			bookFormat,
			percentile => onProgress(30 + (percentile * 0.7)),
		))).then(pages => {
			console.log(`${Math.ceil(pages.length / bookFormat.pagesCount)} books generated with ${pages.length} pages`);

			if(DISPLAY_LOG) {
				allMessages.forEach(message => {
					if(!checkMessageIntegrity(message, pages)) {
						console.error('Missing message part', message);
						// return rejectRecursive(new Error('MissingMessagePart'));
					}
				});
			}

			if(pages.length < 30) {
				const pagesCountToFill = 30 - pages.length;
				console.log('Fill with blank pages', pages.length, pagesCountToFill);
				for(let pageI = 0; pageI < pagesCountToFill; pageI += 1) {
					pages.push({
						count: 0,
						messages: [],
					});
				}
			}

			pages = pages.map((page, index) => {
				// console.log(`PAGE:::${index} Reset messages order`);
				const messages = [];
				// console.log(`${page.messages.length} messages in page`);
				page.messages.forEach((message, i) => {
					if(message) {
						const existing = messages[message.id];
						messages[message.id] = {
							...message,
							text: `${existing ? `${existing.text}\n` : ''}${message.text}`,
							attachments: [...(existing?.attachments || []), ...(message.attachments || [])],
							linesCount: (existing?.linesCount || 0) + (message.linesCount || 0),
							photoSize: message.photoSize || existing?.photoSize || null,
							// Garder le lineIndex du 1er sous-message (détecte vraie coupure inter-pages)
							lineIndex: existing ? existing.lineIndex : message.lineIndex,
							// Le titre de date est décidé sur le 1er sous-message ; les continuations
							// le portent à false et effaceraient la décision budgétée, ce qui
							// supprimerait le titre de tout message assez long pour être découpé.
							showDateTitle: existing ? existing.showDateTitle : message.showDateTitle,
						};
						if(i === 0 || i + 1 === page.messages.length) {
							// console.log(message.id, messages[message.id].text);
						}
					}
				});

				DISPLAY_LOG && console.log(`${messages.length} in page after reset (Reset of ${page.messages.length - messages.length})`);
				return {
					...page,
					messages: messages.filter(message => message),
				};
			});

			// Set event pages count
			if((pages.length + BOOK_PAGES_EXTRA_COUNT) % 2 !== 0) {
				pages.push({ messages: [] });
			}
			resolve(pages);
		}).catch(reject);
    });
}

function getBestofMessagesSelection(messages, options = { format: BOOK_FORMATS_AVAILABLE[0] }) {
	return new Promise((resolve, reject) => {
		// Regroupe les messages en "sessions" selon un gap d'inactivité puis
		// calcule un score par session pour sélectionner les meilleures.
		const sessionGapMinutes = Math.max(1, parseInt(60 * 4, 10));
		const topPercent = Math.min(1, Math.max(0, 0.3));
		const minSessions = Math.max(1, parseInt(3, 10));
		const maxSessions = null; // bestof.maxSessions ? Math.max(minSessions, parseInt(bestof.maxSessions, 10)) : null;
		const forcedSessionsCount = null; // bestof.sessionsCount ? Math.max(1, parseInt(bestof.sessionsCount, 10)) : null;
		const keepChronological = true; // bestof.keepChronological !== false; // par défaut vrai

		// Stopwords FR/EN minimalistes (évite de compter des mots peu informatifs)
		const STOPWORDS = new Set([
			// FR
			'le', 'la', 'les', 'de', 'des', 'du', 'un', 'une', 'et', 'ou', 'a', 'à', 'au', 'aux', 'en', 'dans', 'sur', 'sous', 'par', 'pour', 'pas', 'ne', 'que', 'qui', 'quoi', 'dont', 'où', 'avec', 'sans', 'mais', 'plus', 'moins', 'ce', 'cet', 'cette', 'ces', 'se', 'sa', 'son', 'ses', 'nos', 'vos', 'leurs', 'leur', 'tu', 'te', 'toi', 'il', 'elle', 'on', 'nous', 'vous', 'ils', 'elles', 'y', 'd', 'l', 'j', 'n', 'm', 't', 's', 'qu', 'est', 'été', 'être', 'avoir', 'ai', 'as', 'avons', 'avez', 'ont', 'vais', 'va', 'allons', 'allez', 'vont', 'fait', 'fais', 'faisons', 'faites', 'font', 'comme', 'si', 'alors', 'tres', 'très', 'bien', 'bon', 'bonne', 'vos', 'vos', 'mes', 'tes',
			// EN
			'the', 'a', 'an', 'and', 'or', 'in', 'on', 'at', 'for', 'of', 'to', 'from', 'by', 'with', 'without', 'as', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'it', 'its', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'we', 'they', 'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'hers', 'our', 'their', 'not', 'no', 'yes', 'do', 'does', 'did', 'have', 'has', 'had', 'than', 'then', 'so', 'very', 'good', 'well',
		]);

		const imageOrMediaExt = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'mov', 'avi', 'mkv', 'webm', 'opus', 'mp3', 'wav', 'ogg'];
		const imageUrlRegex = new RegExp(`\\.(${imageOrMediaExt.join('|')})(\\?.*)?$`, 'i');
		const urlRegex = /https?:\/\/\S+/gi;
		const emojiRegex = /([\u231A-\u231B]|[\u23E9-\u23F3]|[\u23F8-\u23FA]|[\u24C2]|[\u25AA-\u25AB]|[\u25B6]|[\u25C0]|[\u25FB-\u25FE]|[\u2600-\u27BF]|[\u2934-\u2935]|[\u2B05-\u2B07]|[\u2B1B-\u2B1C]|[\u2B50]|[\u2B55]|\u3030|\u303D|\u3297|\u3299|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDFFF])/g;

		const getMsgText = m => (getConversationMessageText(m, false) || '').trim();

		const countSignificantWordsCapped = text => {
			if(!text) return 0;
			const sanitized = text
				.replace(urlRegex, ' ') // exclure les URLs du comptage de mots
				.replace(/[^\p{L}\p{N}\s']/gu, ' ') // conserver lettres/chiffres Unicode
				.toLowerCase();
			const tokens = sanitized.split(/\s+/).filter(Boolean);
			let count = 0;
			for(let i = 0; i < tokens.length && count < 50; i += 1) {
				const tk = tokens[i];
				if(tk.length >= 4 && !STOPWORDS.has(tk)) {
					count += 1;
				}
			}
			return Math.min(count, 50);
		};

		const countEmojis = text => {
			if(!text) return 0;
			const m = text.match(emojiRegex);
			return m ? m.length : 0;
		};

		const countMediaInText = text => {
			if(!text) return 0;
			const urls = (text.match(urlRegex) || []);
			return urls.reduce((acc, u) => acc + (imageUrlRegex.test(u) ? 1 : 0), 0);
		};

		// 1) Trier les messages par date ascendante et ignorer ceux sans date valide
		const sortedMessages = sortMessagesByDate((messages || []).filter(isMessageDateValid));

		if(sortedMessages.length === 0) {
			resolve([]);
			return;
		}

		// 2) Découper en sessions selon un gap d'inactivité
		const sessions = [];
		let current = null;
		sortedMessages.forEach(message => {
			if(!current) {
				current = { messages: [message], start: new Date(message.date), end: new Date(message.date) };
			} else {
				const prev = current.messages[current.messages.length - 1];
				const diffMin = Math.max(0, Math.round((new Date(message.date) - new Date(prev.date)) / 60000));
				if(diffMin > sessionGapMinutes) {
					sessions.push(current);
					current = { messages: [message], start: new Date(message.date), end: new Date(message.date) };
				} else {
					current.messages.push(message);
					current.end = new Date(message.date);
				}
			}
		});

		if(current) {
			sessions.push(current);
		}

		// 3) Calculer métriques par session + densité, richesse, engagement (bruts)
		const sessionStats = sessions.map(session => {
			const messagesCount = session.messages.length;
			const durationMinutes = Math.max(1, Math.round((session.end - session.start) / 60000)); // en minutes, min 1

			let mediaCount = 0; // médias/images
			let significantWordCount = 0; // mots significatifs capés à 50/msg
			let emojiCount = 0; // emojis

			const perSender = {};
			for(let mi = 0; mi < session.messages.length; mi += 1) {
				const m = session.messages[mi];
				const txt = getMsgText(m);
				const attachmentsCount = (m.attachments || []).length;
				mediaCount += attachmentsCount + countMediaInText(txt);
				significantWordCount += countSignificantWordsCapped(txt);
				emojiCount += countEmojis(txt);

				let senderKey = 'unknown';
				if(m && Object.prototype.hasOwnProperty.call(m, 'sender') && m.sender != null) {
					senderKey = m.sender;
				} else if(m && Object.prototype.hasOwnProperty.call(m, 'isSender')) {
					senderKey = m.isSender ? 'A' : 'B';
				}
				perSender[senderKey] = (perSender[senderKey] || 0) + 1;
			}

			// Engagement généralisé pour k participants actifs
			// k = nb de participants ayant envoyé au moins 1 message
			// E = 1 - (Somme_i |(N_i/N) - (1/k)|) / (2*(k-1)/k)
			let engagementScore = 0;
			const activeParticipants = Object.keys(perSender).length;
			if(activeParticipants > 1 && messagesCount > 0) {
				const idealShare = 1 / activeParticipants;
				let sumAbsoluteDiffs = 0;
				const values = Object.values(perSender);
				for(let ci = 0; ci < values.length; ci += 1) {
					const share = values[ci] / messagesCount;
					sumAbsoluteDiffs += Math.abs(share - idealShare);
				}
				const maxDeviation = (2 * (activeParticipants - 1)) / activeParticipants;
				engagementScore = 1 - (sumAbsoluteDiffs / maxDeviation);
				if(engagementScore < 0) engagementScore = 0;
				if(engagementScore > 1) engagementScore = 1;
			} else {
				// Monologue (k=1) ou aucun message défensif => engagement nul
				engagementScore = 0;
			}

			const density = messagesCount / durationMinutes; // densité brute
			const richness = (mediaCount * 10) + (significantWordCount * 0.1) + (emojiCount * 0.2); // richesse brute

			return { session, messagesCount, durationMinutes, mediaCount, significantWordCount, emojiCount, perSender, density, richness, engagementScore };
		});

		if(sessionStats.length === 0) {
			resolve([]);
			return;
		}

		// 4) Normalisation sur densité et richesse, calcul du score global
		const maxDensity = sessionStats.reduce((mx, s) => Math.max(mx, s.density), 0) || 1;
		const maxRichness = sessionStats.reduce((mx, s) => Math.max(mx, s.richness), 0) || 1;

		// console.log('First & Last sessions NON scored over', sessionStats.length, sessionStats[0], sessionStats[sessionStats.length - 1]);
		let scored = sessionStats.filter(stats => !(stats.perSender[MESSAGE_SYSTEM_SENDER_NAME] && stats.messagesCount === 1));
		scored = scored.map((stats, index) => {
			const densityNorm = stats.density / maxDensity;
			const richnessNorm = stats.richness / maxRichness;

			const score = ((1.5 * densityNorm) + (3 * richnessNorm) + (2 * stats.engagementScore)) * ((!index || index === (scored.length - 1)) > 1 ? 2 : 1);
			return { ...stats, densityNorm, richnessNorm, score };
		}).sort((a, b) => b.score - a.score);
		// console.log('First & Last sessions scored over', scored.length, scored[0], scored[scored.length - 1]);

		// console.log('Sorted sessions stats by date', scored.sort((statsA, statsB) => new Date(statsA.session?.start) - new Date(statsB.session?.start)));

		// 5) Sélection des meilleures sessions ("panier")
		const baseCount = scored.length; // forcedSessionsCount || Math.max(minSessions, Math.ceil(scored.length * topPercent));
		const finalCount = maxSessions ? Math.min(baseCount, maxSessions) : baseCount;
		const selectedSessions = scored.slice(0, Math.max(1, finalCount)).map(s => s.session);

		console.log('SelectedSessions', selectedSessions.length);

		// 6) Tronquer par capacité (469 lignes/page, 400 pages/livre) via getBookPagesRecursive
		let selectedMessages = selectedSessions.flatMap(s => s.messages);
		if(keepChronological) {
			selectedMessages = sortMessagesByDate(selectedMessages);
		}

		// console.log('selectedMessages', selectedMessages.length, selectedMessages[0], selectedMessages[Math.floor(selectedMessages.length / 2)], selectedMessages[selectedMessages.length - 1]);

		// Marquer les messages avec un index d'origine pour reconstituer proprement
		// const indexedSelected = selectedMessages.map((m, idx) => ({ ...m, __origIndex: idx }));

		const bookFormat = BOOK_CONSTANTS[(options || {}).format || BOOK_FORMATS_AVAILABLE[0]];
		// const pageLimit = bookFormat.pagesCount; // 400 (ou 5 en dev)
		// const submessages = getSubmessages(indexedSelected, options);
		// const pages = getBookPagesRecursive(submessages, [], options);

		// // If we have less pages than the limit, we can return the messages as is
		// if(pages.length <= pageLimit) {
		//     return indexedSelected.map(({ __origIndex, ...rest }) => rest);
		// }

		// const allowedPages = pages.slice(0, pageLimit);
		// const beyondPages = pages.slice(pageLimit);
		// const allowedSub = flat(allowedPages);
		// const beyondSub = flat(beyondPages);
		// const allowedSet = new Set(allowedSub.map(m => m.__origIndex));
		// const beyondSet = new Set(beyondSub.map(m => m.__origIndex));
		// const finalOrigIdxSet = new Set([...allowedSet].filter(idx => !beyondSet.has(idx)));

		// console.log('allowedSet', allowedSet.size, 'beyondSet', beyondSet.size, 'finalOrigIdxSet', finalOrigIdxSet.size);

		// const cappedMessages = indexedSelected
		//     .filter(m => finalOrigIdxSet.has(m.__origIndex))
		//     .map(({ __origIndex, ...rest }) => rest);

		// console.log('cappedMessages', cappedMessages.length);
		// return cappedMessages;

		getSubmessages(selectedMessages, { ...options, caller: 'bestofSelection' }).then(submessages => (
			getBookPagesRecursive(submessages, [], options)
		)).then(pages => {
			// console.log('pages', pages.map(page => ({ messages: page.count, lines: page.messages.reduce((acc, message) => acc + (message?.linesCount || 0), 0) })), pages[0].messages[0], pages[pages.length - 1].messages[pages[pages.length - 1].messages.length - 1]);
			const finalMessages = pages.slice(0, bookFormat.pagesCount).reduce((acc, page) => [...acc, ...page.messages], []);
			// console.log('finalMessages for ', bookFormat.pagesCount, 'first pages', finalMessages.length, finalMessages[0], finalMessages[finalMessages.length - 1]);
			resolve(finalMessages);
		}).catch(reject);
	});
}

function getSubmessages(messages, options = { format: BOOK_FORMATS_AVAILABLE[0] }, onProgress = () => {}) {
    const bookFormat = BOOK_CONSTANTS[(options || {}).format || BOOK_FORMATS_AVAILABLE[0]];
    const messagesWithLines = [];

    // Les pseudo-messages tapback iOS ont été rattachés à leur message d'origine
    // (message.reactions) : on les retire du flux pour ne pas recopier le texte en bulle.
    const sourceMessages = messages.filter(message => !message.isTapbackSource);

    let i = 0;
    const addSubmessages = (message, messageIndex) => {
        const canAddAttachments = false;
        const attachments = normalizeMessageAttachments(message.attachments);
        // Toujours normaliser les attachments + retirer les URLs déjà en carte QR
        const messageForPagination = {
            ...message,
            attachments,
            text: attachments.some(a => typeof a === 'string' && /^https?:\/\//i.test(a))
                ? stripQrUrlsFromText(message.text, attachments)
                : message.text,
        };
        // messageIndex = index dans le tableau original, i = compteur de sous-messages
        const messageLines = getMessageLines(messageForPagination, sourceMessages[messageIndex - 1] || null, sourceMessages[messageIndex + 1] || null, options);
        let lineIndex = -1;
        const hasQrAttachments = attachments.some(a => typeof a === 'string' && /^https?:\/\//i.test(a));
        // Ne pas émettre de bulle texte si le texte strippé est vide ou n'est plus qu'un fragment d'URL
        const shouldSkipTextSubmessages = hasQrAttachments && (
            !(messageForPagination.text || '').trim() || isUrlFragmentOnly(messageForPagination.text)
        );
        if(!shouldSkipTextSubmessages) {
            // Sens décidé sur le message entier : une ligne qui commence par un mot latin
            // dans un message arabe ne doit pas changer de sens en cours de bulle.
            const textDirection = getTextDirection(messageLines.lines.join(' '));
            // push plutôt que concat : concat recopiait tout le tableau à chaque message,
            // soit un coût quadratique qui figeait l'onglet sur les grosses conversations.
            messageLines.lines.filter((line, lineI) => line || lineI === 0).forEach(line => {
                if(line) {
                    lineIndex += 1;
                    messagesWithLines.push({
                        ...messageForPagination, // texte nettoyé des URLs QR
                        text: line,
                        textDirection,
                        attachments: canAddAttachments ? (message.attachments || []).slice(0, bookFormat.attachmentCountPerLine) : [],
                        sourceAttachments: attachments,
                        id: i,
                        lineIndex,
                    });
                }
            });
        }
        if(attachments.length /* > bookFormat.attachmentCountPerLine */) {
            // console.log(`${bookFormat.attachmentCountPerLine} images / line`);
            // console.log('base attachments', message.attachments);
            for(let j = 0; j < Math.ceil(attachments.length / bookFormat.attachmentCountPerLine); j += 1) {
                i += 1;
                const multipleAttachmentsSubMessage = {
                    ...messageForPagination,
                    text: '',
                    attachments: attachments.slice(j === 0 ? 0 : (j * bookFormat.attachmentCountPerLine), (j + 1) * bookFormat.attachmentCountPerLine),
                    sourceAttachments: attachments,
                    id: i,
                    lineIndex: j,
                };
                messagesWithLines.push(multipleAttachmentsSubMessage);
            }
        }
        i += 1;
    };

    // Découpage en lots : ce calcul portait sur la totalité des messages d'un coup et
    // monopolisait le thread principal pendant toute sa durée.
    const processChunk = startIndex => {
        const endIndex = Math.min(startIndex + GENERATION_CHUNK_SIZE, sourceMessages.length);
        for(let messageIndex = startIndex; messageIndex < endIndex; messageIndex += 1) {
            addSubmessages(sourceMessages[messageIndex], messageIndex);
        }

        if(endIndex >= sourceMessages.length) {
            onProgress(100);
            console.log(`${messagesWithLines.length} sub messages (with lines count) / ${options.caller || ''}`);
            return Promise.resolve(messagesWithLines);
        }

        onProgress((endIndex / sourceMessages.length) * 100);
        if(isGenerationCancelled(options)) {
            return Promise.reject(new Error(BOOK_GENERATION_CANCELLED));
        }
        return yieldToScheduler().then(() => processChunk(endIndex));
    };

    return processChunk(0);
}

export function getConversationMessageText(message, remplaceBackSpace = true) {
    const text = message && message.text ? (remplaceBackSpace ? nl2br(message.text) : message.text) : '';
    try {
        return decodeURIComponent(text);
    } catch (err) {
        DISPLAY_LOG && console.error(err, message, message.text);
        return text;
    }
}

export function getLinksInMessage(text) {
	const urlRegex = /http(s)?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?/g;
	const links = (text || '').match(urlRegex);
	return links || [];
}

/** Normalise attachments (array, string CSV « a|||b », null) → tableau de strings. */
export function normalizeMessageAttachments(attachments) {
	if(!attachments) return [];
	if(Array.isArray(attachments)) {
		return attachments
			.filter(Boolean)
			.map(item => (typeof item === 'string' ? item.replace(/\r/g, '').trim() : item))
			.filter(item => (typeof item !== 'string' || item));
	}
	if(typeof attachments === 'string') return getMessageAttachments(attachments);
	return [];
}

// Type d'une pièce jointe déduit de son extension (ou d'une URL) : photo / video /
// audio / link / other. Utilisé par les filtres de l'écran d'édition des messages.
export function getAttachmentMediaType(attachment) {
	if(!attachment) {
		return 'other';
	}
	if(/^https?:\/\//i.test(attachment)) {
		return 'link';
	}
	const ext = `${attachment}`.split('.').pop().toLowerCase();
	if(['jpg', 'jpeg', 'png', 'webp', 'heic', 'bmp'].includes(ext)) {
		return 'photo';
	}
	if(['mp4', 'mov', 'webm', 'avi', 'mkv', 'gif'].includes(ext)) {
		return 'video';
	}
	if(['mp3', 'opus', 'ogg', 'wav', 'm4a', 'aac'].includes(ext)) {
		return 'audio';
	}
	return 'other';
}

/**
 * Détecte si un texte (ou une ligne) n'est qu'un fragment orphelin d'URL longue,
 * tel que `d=PAdGRzdg…` après coupure d'une URL par saut de ligne.
 * Critères : pas de lettre accentuée/espace/ponctuation normale, longueur > 10,
 * contient uniquement des caractères valides dans un path/query d'URL.
 */
export function isUrlFragmentOnly(text) {
	const t = (text || '').trim();
	if(!t || t.length < 5) return false;
	// Une URL complète (avec schéma) n'est pas un fragment
	if(/^https?:\/\//i.test(t)) return false;
	// Si le texte contient des espaces → c'est du texte normal, pas un fragment
	if(/\s/.test(t)) return false;
	// Fragment d'URL typique : uniquement des caractères valides dans un path/query d'URL
	return /^[A-Za-z0-9=&%_.~!*'();:@+,/?#[\]$-]+$/.test(t) && !/^[a-z]{1,20}$/i.test(t);
}

/**
 * Retourne le domaine seul d'une URL (sans www.) pour affichage discret sous la carte QR.
 * Ex. « https://www.differentpulsessanctuary.com/?fbclid=…  » → « differentpulsessanctuary.com »
 */
export function formatLinkPreview(url, maxLength = 40) {
	try {
		const hostname = new URL((url || '').trim()).hostname.replace(/^www\./, '');
		return hostname.length > maxLength ? `${hostname.substring(0, maxLength)}…` : hostname;
	} catch {
		return '';
	}
}

/**
 * Retire du texte d'un message les URLs qui ont déjà une carte QR dans les pièces jointes.
 * Évite la redondance URL en clair + QR dans le livre imprimé.
 * La base de données n'est pas modifiée — uniquement le rendu livre.
 *
 * Comparaison souple : origin + pathname uniquement, sans les query params.
 * Cela couvre les variantes de tracking (fbclid, utm_*, etc.) et les différences d'encodage.
 *
 * Gestion des URLs coupées par saut de ligne : certaines plateformes (Facebook) insèrent un \n
 * au milieu d'une URL longue (ex. "https://…?fbclid=PAdG\nRzdg…"). On rejoint ces fragments
 * avant le matching puis on restaure les lignes qui ne contenaient pas l'URL.
 */
export function stripQrUrlsFromText(text, attachments) {
	const normalized = normalizeMessageAttachments(attachments);
	if(!text || !normalized.length) return text;
	const qrLinks = normalized.filter(a => typeof a === 'string' && /^https?:\/\//i.test(a));
	if(!qrLinks.length) return text;

	const normalizeUrlForMatch = url => {
		try {
			const u = new URL(url.trim());
			return `${u.hostname}${u.pathname}`.toLowerCase().replace(/\/$/, '');
		} catch {
			return url.toLowerCase().trim();
		}
	};
	const qrNormalized = qrLinks.map(normalizeUrlForMatch);

	// Rejoindre les URLs coupées par \n avant de matcher :
	// remplace \n entre la fin d'un mot URL et le début d'un autre (sans espace autour)
	let result = String(text).replace(
		/(https?:\/\/[^\s<>]*)\n([^\s<>]+)/gi,
		(_, before, after) => `${before}${after}`,
	);

	// Retirer les URLs complètes si elles correspondent (normalisation souple) à une PJ QR
	result = result.replace(/https?:\/\/[^\s<>\n]+/gi, match => {
		const norm = normalizeUrlForMatch(match);
		return qrNormalized.some(q => q === norm) ? '' : match;
	});

	// Retirer les lignes qui sont des fragments orphelins d'URL (ex. `d=PAdGR…`)
	// uniquement quand le message a des PJ QR link
	result = result
		.split('\n')
		.filter(line => !isUrlFragmentOnly(line))
		.join('\n');

	// Nettoyer les espaces/retours à la ligne orphelins
	result = result.replace(/[ \t]{2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
	return result;
}

export function transformCsvToJson(csv, fileNumber = 1, preserveLine = false) {
    return csv.split('\n').filter(line => line).map((line, index) => {
        const parts = line.split(';');
        const [date, sender, recipient, text, isSender, attachmentsText] = parts;
        const data = { date, sender, recipient, text, isSender, attachments: getMessageAttachments(attachmentsText) };

		data.reference = `${fileNumber || 1}-${index + 1}`;

		// Colonnes 6+ : formatsLinesCount (« a5:3|a4:2 ») et/ou réactions Meta encodées
		// (commence par %5B). On les distingue par leur contenu, sans dépendre de l'ordre,
		// pour rester compatible avec les anciens CSV (6 colonnes) comme les nouveaux.
		let formatsLinesCount = '';
		let reactionsColumn = '';
		for(let i = 6; i < parts.length; i += 1) {
			const part = parts[i];
			if(!part) continue; // eslint-disable-line no-continue
			if(isEncodedReactionsColumn(part)) {
				reactionsColumn = part;
			} else {
				formatsLinesCount = part;
			}
		}

        if(formatsLinesCount) {
			(formatsLinesCount || '').split('|').forEach(linesCount => {
				const [format = BOOK_FORMATS_AVAILABLE[0], count] = linesCount.split(':');
				data[format] = { linesCount: parseInt(count, 10) };
			});
		}

		const reactions = parseReactionsFromCsv(reactionsColumn);
		if(reactions.length) {
			data.reactions = reactions;
		}

        if(preserveLine) {
            data.csv = line;
        }
        return data;
    });
}

// Échantillon suffisant pour trancher, même sur 200 000 messages.
const IMPORT_DIRECTION_SAMPLE_SIZE = 2000;

/**
 * Sens de lecture d'une conversation en cours d'import, sur un échantillon de ses
 * messages. Les messages des providers n'ont en commun que leur ligne CSV, dont la
 * 4e colonne porte le texte encodé.
 */
export function getMessagesBlocksDirection(messagesBlocks = []) {
    const directions = [];
    messagesBlocks.some(block => (block.messages || []).some(message => {
        const encodedText = message.messageCsv ? (`${message.messageCsv}`.split(';')[3] || '') : `${message.text || ''}`;
        let text = encodedText;
        try {
            text = decodeURIComponent(encodedText);
        } catch (err) {
            // Texte déjà décodé (contient un % isolé) : on le lit tel quel.
        }
        directions.push(getTextDirection(text));
        return directions.length >= IMPORT_DIRECTION_SAMPLE_SIZE;
    }));
    return getDominantDirection(directions);
}

// Nom expéditeur : affiché uniquement au 1er message de ce contact sur la page
// (changement d'expéditeur ou début de page — pas à chaque minute contrairement à l'ancien displayHeader)
function shouldShowSenderName(message, prevMessage) {
    if(message.lineIndex > 0) return false; // sous-message de continuation : pas de nom
    if(!prevMessage || !prevMessage.sender) return true; // premier sur la page
    return message.sender !== prevMessage.sender; // changement d'expéditeur
}

/**
 * Un titre de date s'affiche-t-il au-dessus de ce message ?
 *
 * Règle unique, partagée par le budget de pagination (getMessageLines) et le rendu : le
 * template ne décide plus lui-même, il lit la décision transportée par le message
 * (showDateTitle, posé par getBookPagesRecursive).
 *
 * Cette séparation était la cause des pages blanches : le template comparait des jours
 * obtenus via moment(date, 'YYYY-MM-DD HH:mm:ss'), qui lit l'horodatage en heure locale
 * en ignorant son fuseau, alors que la pagination comparait moment(date), qui convertit
 * depuis UTC. Pour un message de fin de soirée, les deux ne voyaient pas le même jour :
 * le titre était rendu sur une page dont le budget ne l'avait pas prévu, et 25 px de
 * débordement suffisent à faire ajouter une feuille blanche par PhantomJS.
 */
export function shouldShowDateTitle(message, prevMessage) {
    if(message.lineIndex) {
        return false; // sous-message de continuation : le titre est déjà passé
    }
    if(!prevMessage || !prevMessage.date) {
        return true; // premier message du livre
    }
    if(!message.date) {
        return false;
    }
    return dateFormat(prevMessage.date, 'YYYYMMDD') !== dateFormat(message.date, 'YYYYMMDD');
}

// Découpe le texte d'un message en lignes de bulle : split sur les retours à la ligne,
// puis césure au dernier espace avant la limite (budget latin, ou largeur visuelle pour
// les écritures à chasse pleine). Source unique partagée par la pagination du livre
// (getMessageLines) et l'aperçu d'édition, pour que les deux coupent aux mêmes endroits.
export function getMessageTextLines(message, format = BOOK_FORMATS_AVAILABLE[0]) {
    const bookFormat = BOOK_CONSTANTS[format || BOOK_FORMATS_AVAILABLE[0]];
    const lines = [];

    getConversationMessageText(message, false).split('\n').forEach(part => {
        // Japonais, coréen, chinois : la césure latine (couper à 47 caractères au dernier
        // espace) produit des lignes deux fois trop larges, car ces écritures sont à chasse
        // pleine et s'écrivent sans espaces. Les bulles étant en white-space: nowrap, la
        // ligne déborderait de la page. On mesure donc la largeur visuelle réelle.
        // Le texte purement latin reste sur le chemin historique, au caractère près.
        if(hasFullWidthChars(part)) {
            wrapTextByVisualWidth(part, BOOK_LINE_MESSAGE_CHARS_COUNT_CJK).forEach(line => lines.push(line));
            return;
        }

        let messageTextLeft = part;
        let trimmedString = '';
        let i = 0;
        const limit = 1000;

        if(messageTextLeft.length > bookFormat.messageCharsCount) {
            while(i <= limit && messageTextLeft.length > bookFormat.messageCharsCount) {
                if(i === limit) {
                    console.log('ERROR', getConversationMessageText(message, false), lines, messageTextLeft, trimmedString);
                    lines.push(messageTextLeft); // Add left messages to lines in case of error
                    notifyError(new Error('ExtremlyLongMessageError'));
                }

                if(messageTextLeft.length > trimmedString.length) {
                    // trim the string to the maximum length
                    trimmedString = messageTextLeft.substr(0, bookFormat.messageCharsCount);

                    // re-trim if we are in the middle of a word and
                    let nextTruncateIndex = Math.min(trimmedString.length, trimmedString.lastIndexOf(' '));
                    if(nextTruncateIndex <= 0) {
                        nextTruncateIndex = bookFormat.messageCharsCount;
                    }
                    trimmedString = trimmedString.substr(0, nextTruncateIndex);
                    lines.push(trimmedString);
                }
                messageTextLeft = messageTextLeft.substr(trimmedString.length, messageTextLeft.length);

                i += 1;
            }
            lines.push(messageTextLeft);
        } else {
            lines.push(messageTextLeft);
        }
    });

    return lines;
}

export function getMessageLines(message, prevMessage, nextMessage, options = { format: BOOK_FORMATS_AVAILABLE[0] }, log = false) {
    const bookFormat = BOOK_CONSTANTS[(options || {}).format || BOOK_FORMATS_AVAILABLE[0]];

	if(message.isBestofSeparator) {
		// Hauteur fixe : padding vertical + 2 lignes texte (aligné sur .bestof-separator CSS)
		const lineCount = Math.ceil(bookFormat.messageLineSize * 3);
		const lead = message.separatorLead || (message.text || '').split('\n')[0] || '…';
		const duration = message.separatorDuration || (message.text || '').split('\n')[1] || '…';
		return {
			lines: [lead, duration],
			count: lineCount,
			senderNameCount: 0,
			dateTitleCount: 0,
			wrapperCount: 0,
			bubbleCount: 0,
			attachmentsCount: 0,
			details: [`Bestof separator: ${lineCount}`],
		};
	}
    let count = 0;
    let dateTitleCount = 0;
    let senderNameCount = 0;
    let wrapperCount = 0;
    let bubbleCount = 0;
    let attachmentsCount = 0;
    const details = [];

    log && console.log(`

        -----

    `, message, prevMessage && 'has prev message', nextMessage && 'has next message');

    // Adding date title lines size
    if(shouldShowDateTitle(message, prevMessage)) {
        log && console.log(`Add ${bookFormat.dateTitleSize} for date title`);
        count += bookFormat.dateTitleSize;
        dateTitleCount = bookFormat.dateTitleSize;
        details.push(`Title: ${bookFormat.dateTitleSize}`);
    // } else if(message.lineIndex === 0 && prevMessage && message) {
    //     console.log(message.text);
    //     console.log(`${dateFormat(prevMessage.date, 'YYYYMMDD')} !== ${dateFormat(message.date, 'YYYYMMDD')} ??`);
    }

    // Nom expéditeur : 1 fois par contact par page (sans l'heure qui est désormais en marge)
    if(shouldShowSenderName(message, prevMessage)) {
        log && console.log(`Add ${bookFormat.messageNameSize} for sender name`);
        count += bookFormat.messageNameSize;
        senderNameCount = bookFormat.messageNameSize;
        details.push(`Name: ${bookFormat.messageNameSize}`);
    }

    // Marge basse de bulle : le CSS applique margin-bottom (messageWrapperSize) à CHAQUE
    // message (.messageWrapper), pas seulement aux changements d'expéditeur. La compter
    // uniquement aux frontières sous-estimait la hauteur des pages où une même personne
    // enchaîne plusieurs messages (24 marges non budgétées sur une page = ~48px), ce qui
    // faisait déborder la page en PDF et créait une page blanche. On la compte donc une
    // fois par message (1er sous-message, lineIndex 0), comme le rendu réel.
    if(!message.lineIndex) {
        log && console.log(`Add ${bookFormat.messageWrapperSize} for message wrapper`);
        count += bookFormat.messageWrapperSize;
        wrapperCount = bookFormat.messageWrapperSize;
        details.push(`Message wrapper: ${bookFormat.messageWrapperSize}`);
    }

    if(!message.lineIndex && message.text && (!nextMessage || (nextMessage && (!nextMessage.lineIndex || message.sender !== nextMessage.sender || message.attachments.length > 0)))) {
        log && console.log(`Add ${bookFormat.messageBubbleSize} for message lines`);
        count += bookFormat.messageBubbleSize;
        bubbleCount = bookFormat.messageBubbleSize;
        details.push(`Message bubble: ${bookFormat.messageBubbleSize}`);
    }

    // Réactions : surcoût compté UNE SEULE FOIS par message, sur le 1er sous-message
    // (lineIndex 0). Ne pas se baser sur nextMessage ici : en pagination, nextMessage
    // est mis à null pour tous les sous-messages d'un même message (même id), ce qui
    // ferait recompter le surcoût sur chaque ligne d'un message multi-lignes.
    if(!message.lineIndex && (message.reactions || []).length && bookFormat.messageReactionSize) {
        count += bookFormat.messageReactionSize;
        details.push(`Message reactions: ${bookFormat.messageReactionSize}`);
    }

    const lines = getMessageTextLines(message, (options || {}).format);

    const messageAttachments = normalizeMessageAttachments(message?.attachments);
    let photoSize = null;
    if(messageAttachments.length > 0) {
		const fontBase = ((options || {}).format === 'a4') ? FONT_BASE_A4 : FONT_BASE_A5;
		// Une photo est budgétée selon la taille retenue pour le livre (S/M/L, cf.
		// bookPhotoLayout) ; les autres pièces jointes gardent leur carte QR forfaitaire.
		const attachmentBudgets = messageAttachments.map(attachment => {
			const layout = resolvePhotoAttachmentLayout(attachment, message, options, fontBase);
			if(layout.tier) {
				return layout;
			}
			const { type } = getAttachmentUrlData({}, message, null, attachment, false);
			return { tier: null, px: bookFormat.attachmentSizes[type] || bookFormat.attachmentSizes.default };
		});
		// Taille transportée jusqu'au template (classe photo-m / photo-l). Un message ne
		// porte qu'une pièce jointe par sous-message (attachmentCountPerLine = 1).
		photoSize = (attachmentBudgets.find(budget => budget.tier) || {}).tier || null;

		let messageAttachmentsSize = 0;
		for(let i = 0; i < Math.ceil(attachmentBudgets.length / bookFormat.attachmentCountPerLine); i += 1) {
			const lineBudgets = attachmentBudgets.slice(i * bookFormat.attachmentCountPerLine, (i + 1) * bookFormat.attachmentCountPerLine);
			messageAttachmentsSize += Math.max(...lineBudgets.map(budget => budget.px));
		}
        count += messageAttachmentsSize;
        attachmentsCount = messageAttachmentsSize;

        details.push(`Message attachments: ${messageAttachmentsSize}${photoSize ? ` (${photoSize})` : ''}`);
    }
    // log && console.log('getMesageLines', message, lines, count);
    // log && console.log(`${lines.length} + ${count} = ${lines.length + count} lignes occupées`);

    // V1
    // return lines
    //     .filter(line => line)
    //     .concat(new Array(Math.max(0, Math.floor(count))).fill(''));

    // V2
    // lines = lines.filter(line => line);
    log && console.log(`Message with ${lines.filter(line => line).length} text lines so (${lines.filter(line => line).length} * ${bookFormat.messageLineSize}) + ${count} design lines = ${Math.max(0, count + (lines.filter(line => line).length * bookFormat.messageLineSize))}`, `"${message.text}"`, lines);

    lines.filter(line => line).length && details.push(`Message lines: ${lines.filter(line => line).length * bookFormat.messageLineSize} (${lines.filter(line => line).length} * ${bookFormat.messageLineSize})`);

    log && console.log(details);
    const textCount = lines.filter(line => line).length * bookFormat.messageLineSize;
    return {
        lines,
        count: Math.max(0, Math.ceil(count + textCount)),
        // Détail chiffré par composant : permet à la pagination de recalculer
        // les coûts dépendants du contexte de page (nom d'expéditeur réaffiché
        // en haut de page, comme le fait le template via seenSenders).
        senderNameCount,
        dateTitleCount,
        wrapperCount,
        bubbleCount,
        attachmentsCount,
        textCount,
        photoSize,
        details,
    };
}

export function getAttachmentUrlData(conversation, message, participant, attachment, displayAllMedias = false) {
    let url = '';
    let type = '';
	let isLink = false;
    if(conversation && message && attachment) {
        const linkRegex = new RegExp('http(s)?://');
		const displayExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
		if(displayAllMedias) {
			displayExtensions.push('mp4', 'mov', 'avi', 'mkv', 'webm', 'opus', 'mp3', 'wav', 'ogg');
		}
        const imageRegex = new RegExp(`[.]{1}(${displayExtensions.join('|')})+`);
        isLink = linkRegex.test(attachment);

        if(!isLink && imageRegex.test(attachment)) { // Second check if media is image (can be displayed)
            url = `conversations/${conversation._id}/images/${attachment}`;
            type = 'image';
        } else { // Finally display QR Code to redirect to media page viewer
            let urlViewer = '';
            if(isLink) {
                // type = 'link';
                type = 'qrcode';
                urlViewer = attachment;
            } else {
                const { key, salt } = getHashKeys(`${conversation._id}.${attachment}`);
                const params = [
                    attachment,
                ].concat([
                        // Titre sans emoji : chaque emoji pèse une douzaine de caractères une
                        // fois percent-encodé et densifie le QR code imprimé à 18 mm.
                        // La couverture, elle, affiche bien les emojis.
                        removeEmojis(`${(conversation.cover || {}).from || ''} ${(conversation.cover || {}).to || ''}`),
                        (participant && (participant.displayName || participant.name)) || '',
                        message.text || '',
                        message.date || '',
                    ]
                    .map(value => `${value}`.replace(/\//gi, '-'))
                    .map(value => `${value}`.replace(/\?/gi, '-'))
                    .map(value => `${value}`.replace(/#/gi, ''))).concat([
                    key,
                    salt,
                ]);
                const viewer = `${config.url}conversation/${conversation._id}/viewer/${params.join(MESSAGE_ATTACHMENT_VIEWER_DATA_SEPARATOR)}`;
                // console.log(`
                //     1: ${viewer}
                //     2: ${encodeURI(viewer)}
                //     3: ${encodeURIComponent(viewer)}
                //     4: ${encodeURIComponent(encodeURI(viewer))}
                //
                // `);
                // process.env.NODE_ENV === 'development' && console.log('attachment viewer url', conversation, encodeURI(viewer), params);
                urlViewer = encodeURIComponent(encodeURI(viewer));
            }
            // const qrCodeApi = 'https://api.qrserver.com/v1/create-qr-code/';
            const qrCodeApi = `${config.url}api/file/qrcode`;
            const size = '100';
            const options = '&color=0c1e58'; // `&color=${textColor.replace('#', '')}&bgcolor=${bgColor.replace('#', '')}&margin=5`; // Use with caution due to bg color contrast
            url = `${qrCodeApi}?size=${size}x${size}${options}&data=${urlViewer}`;
            type = 'qrcode';
            // process.env.NODE_ENV === 'development' && console.log(url);
        }
    }

    return {
        url,
        type,
		isLink,
    };
}

// DEBUG
export function checkMessageIntegrity(message, pages) {
    const functionLogDisplay = false && DISPLAY_LOG;
    const logs = [];

    logs.push(`checkMessageIntegrity / Pages length: ${pages.length}`);
    const spaceEncodedString = /(%(.){2}|\.|-)/;
    let pageIndex = pages.length - 1;
    let firstWordCheck = false;
    let lastWordCheck = false;

    logs.push(`Message initial: ${message.text}`);
    const messageWords = (message.text || '').split(spaceEncodedString);
    const firstWord = messageWords.shift();
    const lastWord = messageWords.pop();
    logs.push(`Message starts with "${firstWord}" and ends with "${lastWord}"`);

    if(firstWord && lastWord) {
        while(pageIndex >= 0 && (!firstWordCheck || !lastWordCheck)) {
            logs.push(`PageIndex: ${pageIndex}`);
            if(pages[pageIndex] && pages[pageIndex].messages && pages[pageIndex].messages.length) {
                // functionLogDisplay && console.log(pages[pageIndex].messages);

                // First word check
                if(pages[pageIndex].messages.some(subMessage => {
                    const subMessageText = subMessage.text.replace(spaceEncodedString, '');
                    logs.push(`Sub message for first word ("${firstWord}"): "${subMessage.text.substring(0, 10)}  ...  ${subMessage.text.substring(subMessage.text.length - 10, subMessage.text.length)}" / Index of firstWord: ${subMessage.text.indexOf(firstWord)}`);
                    return subMessageText.indexOf(firstWord) === 0;
                })) {
                    firstWordCheck = true; // Can be one a distinct page of lastWord
                }

                // Last word check
                if(pages[pageIndex].messages.some(subMessage => {
                    const subMessageText = subMessage.text.replace(spaceEncodedString, '');
                    logs.push(`Sub message for last word ("${lastWord}"): "${subMessage.text.substring(0, 10)}  ...  ${subMessage.text.substring(subMessage.text.length - 10, subMessage.text.length)}" / Index of lastWord: ${subMessageText.lastIndexOf(lastWord)}`);
                    return subMessageText.lastIndexOf(lastWord) === subMessageText.length - lastWord.length;
                })) {
                    lastWordCheck = true; // Can be one a distinct page of firstWord
                }
            } else {
                // console.log(`Page does not exist at index ${pageIndex}`, pages);
            }
            // functionLogDisplay && console.log('Decrement page index');
            // functionLogDisplay && console.log(`Loop again: ${pageIndex >= 0 && (!firstWordCheck || !lastWordCheck)} === ${pageIndex} >= 0 && (!${firstWordCheck} || !${lastWordCheck})`);
            pageIndex -= 1;
        }
        functionLogDisplay && console.log(`firstWordCheck: ${firstWordCheck}`);
        functionLogDisplay && console.log(`lastWordCheck: ${lastWordCheck}`);
        !(firstWordCheck && lastWordCheck) && console.error(`${firstWord} (${firstWordCheck}) ... ${lastWord} (${lastWordCheck})`, message, logs, pages.reduce((pl, p) => (`${pl} // ${p.messages.reduce((ml, m) => (`${ml} / ${(m || {}).text || ''}`), '')}`), ''));
        return firstWordCheck && lastWordCheck;
    }
    return true;
}
